// Storage Service for offline-first photo handling with R2 sync
// Photos are saved locally first, then synced to Cloudflare R2 when online

import { photoDb, db } from '../db/database'
import { photoApi, convertToJPEG } from './apiService'

// Track sync status with timeout protection
let isSyncing = false
let syncStartTime = null
let syncListeners = []
const SYNC_TIMEOUT_MS = 60000 // 60 second timeout for stuck syncs

/**
 * Register a listener for sync status changes
 * @param {Function} callback - Called with { syncing, pending, error }
 */
export function onSyncStatusChange(callback) {
  syncListeners.push(callback)
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback)
  }
}

function notifySyncStatus(status) {
  syncListeners.forEach(cb => cb(status))
}

/**
 * Save photo locally first (offline-first approach)
 * @param {Object} photoData - Photo data including surveyId, categoryCode, blob
 * @returns {Object} Local photo record
 */
export async function savePhotoLocally(photoData) {
  const { surveyId, categoryCode, blob, thumbnailBlob, notes = '' } = photoData

  // Convert blobs to base64 for IndexedDB storage
  const imageData = blob ? await blobToBase64(blob) : null
  const thumbnailData = thumbnailBlob ? await blobToBase64(thumbnailBlob) : null

  // Save to IndexedDB
  const localPhoto = await photoDb.add({
    surveyId,
    categoryCode,
    imageData,
    thumbnailData,
    notes,
    analyseWithAI: true,
    uploaded: false,
    r2Key: null,
    remoteId: null  // Will be set after successful upload
  })

  return localPhoto
}

/**
 * Upload a single photo to R2
 * @param {Object} photo - Local photo object from IndexedDB
 * @returns {Object} Upload result with remote photo data
 */
export async function uploadToR2(photo) {
  try {
    // Convert base64 back to blob for upload
    const imageBlob = await base64ToBlob(photo.imageData)
    const thumbnailBlob = photo.thumbnailData ? await base64ToBlob(photo.thumbnailData) : null

    // Convert to JPEG for PDF compatibility
    let jpegBlob = imageBlob
    if (imageBlob.type !== 'image/jpeg') {
      jpegBlob = await convertToJPEG(imageBlob)
    }

    let jpegThumb = thumbnailBlob
    if (thumbnailBlob && thumbnailBlob.type !== 'image/jpeg') {
      jpegThumb = await convertToJPEG(thumbnailBlob, 0.8)
    }

    // Upload via API
    const remotePhoto = await photoApi.upload(
      photo.surveyId,
      photo.categoryCode,
      jpegBlob,
      jpegThumb
    )

    // Update notes if any
    if (photo.notes) {
      await photoApi.update(remotePhoto.id, { notes: photo.notes })
    }

    // Mark as uploaded in IndexedDB
    await photoDb.markUploaded(photo.id, remotePhoto.r2Key)

    // Store remote ID for reference
    await db.photos.update(photo.id, { remoteId: remotePhoto.id })

    return {
      success: true,
      localId: photo.id,
      remotePhoto
    }
  } catch (error) {
    console.error('Failed to upload photo to R2:', error)
    return {
      success: false,
      localId: photo.id,
      error: error.message
    }
  }
}

/**
 * Sync all pending photos to R2
 * @param {Function} onProgress - Progress callback (current, total)
 * @returns {Object} Sync results
 */
export async function syncPhotosToR2(onProgress, forceReset = false) {
  // Check for stuck sync (timeout protection)
  if (isSyncing && !forceReset) {
    const elapsed = syncStartTime ? Date.now() - syncStartTime : 0
    if (elapsed < SYNC_TIMEOUT_MS) {
      return { success: false, message: 'Sync already in progress' }
    }
    // Sync has been running too long - reset it
    console.warn('Sync timeout detected, resetting sync state')
    isSyncing = false
  }

  if (!navigator.onLine) {
    return { success: false, message: 'No internet connection' }
  }

  isSyncing = true
  syncStartTime = Date.now()
  notifySyncStatus({ syncing: true, pending: 0, error: null })

  try {
    // Get all pending photos from IndexedDB
    const allPhotos = await db.photos.toArray()
    const pendingPhotos = allPhotos.filter(p => !p.uploaded)

    if (pendingPhotos.length === 0) {
      isSyncing = false
      syncStartTime = null
      notifySyncStatus({ syncing: false, pending: 0, error: null })
      return { success: true, synced: 0, message: 'All photos already synced' }
    }

    notifySyncStatus({ syncing: true, pending: pendingPhotos.length, error: null })

    let synced = 0
    let failed = 0
    const errors = []

    for (let i = 0; i < pendingPhotos.length; i++) {
      const photo = pendingPhotos[i]

      if (onProgress) {
        onProgress(i + 1, pendingPhotos.length)
      }

      const result = await uploadToR2(photo)

      if (result.success) {
        synced++
      } else {
        failed++
        errors.push({ photoId: photo.id, error: result.error })
      }

      notifySyncStatus({
        syncing: true,
        pending: pendingPhotos.length - i - 1,
        error: null
      })
    }

    isSyncing = false
    syncStartTime = null
    notifySyncStatus({ syncing: false, pending: failed, error: errors.length > 0 ? errors[0].error : null })

    return {
      success: failed === 0,
      synced,
      failed,
      errors,
      message: failed === 0
        ? `Successfully synced ${synced} photos`
        : `Synced ${synced} photos, ${failed} failed`
    }
  } catch (error) {
    isSyncing = false
    syncStartTime = null
    notifySyncStatus({ syncing: false, pending: 0, error: error.message })
    return {
      success: false,
      error: error.message,
      message: 'Sync failed: ' + error.message
    }
  }
}

/**
 * Get count of pending (unsynced) photos
 * @returns {number} Count of pending photos
 */
export async function getPendingCount() {
  const allPhotos = await db.photos.toArray()
  return allPhotos.filter(p => !p.uploaded).length
}

/**
 * Get all photos for a survey from local DB
 * @param {string} surveyId - Survey ID
 * @returns {Array} Photos array
 */
export async function getPhotosForSurvey(surveyId) {
  return photoDb.getBySurvey(surveyId)
}

/**
 * Delete a photo (both local and remote)
 * @param {number} localId - Local photo ID
 * @param {string} remoteId - Remote photo ID (if uploaded)
 */
export async function deletePhoto(localId, remoteId) {
  // Delete from R2 if uploaded
  if (remoteId) {
    try {
      await photoApi.delete(remoteId)
    } catch (error) {
      console.warn('Failed to delete remote photo:', error)
    }
  }

  // Delete from IndexedDB
  await photoDb.delete(localId)
}

// Helper: Convert blob to base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Helper: Convert base64 to blob
function base64ToBlob(base64) {
  return new Promise((resolve) => {
    const [header, data] = base64.split(',')
    const mimeMatch = header.match(/data:([^;]+)/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'

    const binary = atob(data)
    const array = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i)
    }

    resolve(new Blob([array], { type: mime }))
  })
}

/**
 * Cache a remote photo to local IndexedDB
 * Fetches the image from R2 and stores it locally for offline use
 * @param {Object} photo - Remote photo object with imageUrl
 * @param {number} surveyId - Local survey ID
 * @returns {Object} Photo object with imageData added
 */
export async function cacheRemotePhoto(photo, surveyId) {
  try {
    // Check if already cached locally
    const existing = await db.photos.where('remoteId').equals(photo.id).first()
    if (existing && existing.imageData) {
      // Already cached - return existing data
      return {
        ...photo,
        imageData: existing.imageData,
        thumbnailData: existing.thumbnailData,
        localId: existing.id
      }
    }

    // Fetch image from R2
    const response = await fetch(photo.imageUrl)
    if (!response.ok) {
      console.warn(`Failed to fetch photo ${photo.id}: ${response.status}`)
      return photo
    }

    const blob = await response.blob()
    const imageData = await blobToBase64(blob)

    // Fetch thumbnail if available
    let thumbnailData = null
    if (photo.thumbnailUrl) {
      try {
        const thumbResponse = await fetch(photo.thumbnailUrl)
        if (thumbResponse.ok) {
          const thumbBlob = await thumbResponse.blob()
          thumbnailData = await blobToBase64(thumbBlob)
        }
      } catch (e) {
        console.warn('Failed to fetch thumbnail:', e)
      }
    }

    // Save to IndexedDB
    const localPhoto = await photoDb.add({
      surveyId,
      categoryCode: photo.categoryCode,
      imageData,
      thumbnailData,
      notes: photo.notes || '',
      analyseWithAI: photo.analyseWithAI !== false,
      uploaded: true, // Already on R2
      r2Key: photo.r2Key,
      remoteId: photo.id
    })

    return {
      ...photo,
      imageData,
      thumbnailData,
      localId: localPhoto.id
    }
  } catch (error) {
    console.error('Failed to cache photo:', error)
    return photo // Return original without cache
  }
}

/**
 * Check if online and R2 available
 * @returns {boolean} Availability status
 */
export function isR2Available() {
  return navigator.onLine
}

/**
 * Get current sync status
 */
export function getSyncStatus() {
  return { syncing: isSyncing }
}

export default {
  savePhotoLocally,
  uploadToR2,
  syncPhotosToR2,
  getPendingCount,
  getPhotosForSurvey,
  deletePhoto,
  onSyncStatusChange,
  isR2Available,
  getSyncStatus,
  cacheRemotePhoto
}
